import { KIOSK_PRO_MEDIA } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-pro-shared.styles';
import { BAR_TEST_ID, BUTTON_TEST_IDS } from './constants';
import { describeElement, getRectSnapshot, round } from './utils';
import type { AncestorTransform, BarStylesSnapshot, EnvSnapshot, OverlaySnapshot, RectSnapshot } from './types';

/**
 * Collects a comprehensive, privacy-safe environment and layout snapshot.
 * Captures screen, viewport, safe areas, media queries, bar layout/computed styles,
 * transformed ancestors, and open overlay/modal layers.
 */
export const collectEnvSnapshot = (reason: string): EnvSnapshot => {
  const isClient = typeof window !== 'undefined';

  const screenData = isClient
    ? {
        width: round(window.screen.width),
        height: round(window.screen.height),
        availWidth: round(window.screen.availWidth),
        availHeight: round(window.screen.availHeight),
        orientation: (window.screen as Partial<Screen>).orientation?.type,
        dpr: round(window.devicePixelRatio),
      }
    : {
        width: 0,
        height: 0,
        availWidth: 0,
        availHeight: 0,
        orientation: undefined,
        dpr: 1,
      };

  const windowData = isClient
    ? {
        innerWidth: round(window.innerWidth),
        innerHeight: round(window.innerHeight),
        outerWidth: round(window.outerWidth),
        outerHeight: round(window.outerHeight),
        clientWidth: round(document.documentElement.clientWidth),
        clientHeight: round(document.documentElement.clientHeight),
      }
    : {
        innerWidth: 0,
        innerHeight: 0,
        outerWidth: 0,
        outerHeight: 0,
        clientWidth: 0,
        clientHeight: 0,
      };

  const scrollData = isClient
    ? {
        x: round(window.scrollX),
        y: round(window.scrollY),
      }
    : { x: 0, y: 0 };

  const vv = isClient ? window.visualViewport : null;
  const visualViewportData = vv
    ? {
        width: round(vv.width),
        height: round(vv.height),
        offsetLeft: round(vv.offsetLeft),
        offsetTop: round(vv.offsetTop),
        pageLeft: round(vv.pageLeft),
        pageTop: round(vv.pageTop),
        scale: round(vv.scale),
      }
    : null;

  const viewportMeta = isClient
    ? document.querySelector('meta[name="viewport"]')?.getAttribute('content') ?? null
    : null;

  const mediaQueries: Record<string, boolean> = {};
  if (isClient && typeof window.matchMedia === 'function') {
    const queries: Array<[string, string]> = [
      ['pointer_coarse', '(pointer: coarse)'],
      ['pointer_fine', '(pointer: fine)'],
      ['hover_hover', '(hover: hover)'],
      ['hover_none', '(hover: none)'],
      ['display_standalone', '(display-mode: standalone)'],
      ['display_fullscreen', '(display-mode: fullscreen)'],
      ['orientation_portrait', '(orientation: portrait)'],
      ['orientation_landscape', '(orientation: landscape)'],
      ['kiosk_maxWidth', KIOSK_PRO_MEDIA.maxWidth],
      ['kiosk_landscapeMaxWidth', KIOSK_PRO_MEDIA.landscapeMaxWidth],
      ['kiosk_portraitMaxWidth', KIOSK_PRO_MEDIA.portraitMaxWidth],
      ['kiosk_smallViewportPortrait', KIOSK_PRO_MEDIA.smallViewportPortrait],
      ['kiosk_smallViewportLandscape', KIOSK_PRO_MEDIA.smallViewportLandscape],
      ['kiosk_smallViewportLandscapeOnly', KIOSK_PRO_MEDIA.smallViewportLandscapeOnly],
    ];
    queries.forEach(([key, q]) => {
      try {
        mediaQueries[key] = window.matchMedia(q).matches;
      } catch {
        mediaQueries[key] = false;
      }
    });
  }

  const getElementStyles = (
    el: HTMLElement | null
  ): { overflow: string; position: string; touchAction: string; height: string; minHeight: string } => {
    if (!el || !isClient || typeof window.getComputedStyle !== 'function') {
      return { overflow: '', position: '', touchAction: '', height: '', minHeight: '' };
    }
    const cs = window.getComputedStyle(el);
    return {
      overflow: cs.overflow,
      position: cs.position,
      touchAction: cs.touchAction,
      height: cs.height,
      minHeight: cs.minHeight,
    };
  };

  const bodyStyles = isClient
    ? getElementStyles(document.body)
    : { overflow: '', position: '', touchAction: '', height: '', minHeight: '' };
  const htmlStyles = isClient
    ? getElementStyles(document.documentElement)
    : { overflow: '', position: '', touchAction: '', height: '', minHeight: '' };

  const bar = isClient ? document.querySelector<HTMLElement>(`[data-testid="${BAR_TEST_ID}"]`) : null;

  let barRect: RectSnapshot | undefined;
  let barStyles: BarStylesSnapshot | undefined;
  const barButtons: Record<string, { rect: RectSnapshot; pointerEvents: string; display: string }> = {};
  const ancestorTransforms: AncestorTransform[] = [];

  if (bar) {
    barRect = getRectSnapshot(bar);
    if (typeof window.getComputedStyle === 'function') {
      const bs = window.getComputedStyle(bar);
      barStyles = {
        position: bs.position,
        top: bs.top,
        bottom: bs.bottom,
        left: bs.left,
        right: bs.right,
        transform: bs.transform,
        zIndex: bs.zIndex,
        pointerEvents: bs.pointerEvents,
        opacity: bs.opacity,
        willChange: bs.willChange,
        contain: bs.contain,
      };

      BUTTON_TEST_IDS.forEach((btnTestId) => {
        const btn = bar.querySelector(`[data-testid="${btnTestId}"]`);
        if (btn) {
          const btnStyle = window.getComputedStyle(btn);
          barButtons[btnTestId] = {
            rect: getRectSnapshot(btn),
            pointerEvents: btnStyle.pointerEvents,
            display: btnStyle.display,
          };
        }
      });

      // Scan ancestor chain for non-default transform/contain/filter properties
      // that would create a new containing block for position: fixed elements.
      let curr = bar.parentElement;
      while (curr && curr !== document.body && curr !== document.documentElement) {
        try {
          const cs = window.getComputedStyle(curr);
          const hasTransform = Boolean(cs.transform && cs.transform !== 'none');
          const hasFilter = Boolean(cs.filter && cs.filter !== 'none');
          const hasPerspective = Boolean(cs.perspective && cs.perspective !== 'none');
          const hasContain = Boolean(cs.contain && cs.contain !== 'none' && cs.contain !== 'normal');
          const hasWillChange = Boolean(cs.willChange && cs.willChange !== 'auto');

          if (hasTransform || hasFilter || hasPerspective || hasContain || hasWillChange) {
            ancestorTransforms.push({
              descriptor: describeElement(curr),
              transform: cs.transform,
              filter: cs.filter,
              perspective: cs.perspective,
              contain: cs.contain,
              willChange: cs.willChange,
            });
          }
        } catch {
          // no-op
        }
        curr = curr.parentElement;
      }
    }
  }

  const openOverlays: OverlaySnapshot[] = [];
  if (isClient && typeof window.getComputedStyle === 'function') {
    try {
      const selectors = [
        '.ReactModal__Overlay',
        '[role="dialog"]',
        '.MuiBackdrop-root',
        '[class*="backdrop" i]',
        '[class*="modal" i]',
      ];
      const candidates = document.querySelectorAll(selectors.join(','));
      const seen = new Set<Element>();
      for (let i = 0; i < candidates.length && openOverlays.length < 5; i += 1) {
        const el = candidates[i];
        if (!seen.has(el)) {
          seen.add(el);
          const cs = window.getComputedStyle(el);
          openOverlays.push({
            descriptor: describeElement(el),
            rect: getRectSnapshot(el),
            pointerEvents: cs.pointerEvents,
            visibility: cs.visibility,
            display: cs.display,
            zIndex: cs.zIndex,
          });
        }
      }
    } catch {
      // no-op
    }
  }

  const nav = isClient ? window.navigator : null;

  return {
    reason,
    ua: nav ? nav.userAgent : '',
    userAgentData: nav ? (nav as { userAgentData?: unknown }).userAgentData : undefined,
    maxTouchPoints: nav ? nav.maxTouchPoints : 0,
    screen: screenData,
    window: windowData,
    scroll: scrollData,
    visualViewport: visualViewportData,
    viewportMeta,
    mediaQueries,
    bodyStyles,
    htmlStyles,
    bar: {
      found: Boolean(bar),
      rect: barRect,
      styles: barStyles,
      buttons: barButtons,
    },
    ancestorTransforms,
    openOverlays,
  };
};
